/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { storage } from '@uhg-abyss/web/tools/storage';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { SearchFilterContext } from '../../../context/SearchFilterContext';
import {
  useEducationalContentStatus,
  useSaveEducationalContentStatus,
} from '../../../hooks/useEducationalContent';
import { useHsidUuid } from '../../../hooks/useHsidUuid';
import { useLagoon } from '../../../hooks/useLagoon';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { useIsEligibleForStandaloneDental } from '../../../utils/user.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import {
  CareCategories,
  Constants,
  OPTUM_CORRELATION_ID_HEADER,
} from '../../Constants';
import { phoneOnly } from '../../ConstantsStyles';
import { handleLinkAndModalTrack } from '../../Utils/adobeTrackUtils';
import { getFeatureFlag } from '../../Utils/getFeatureFlag';
import {
  Dob,
  WelcomeMessageScreen,
  memberSelectorButtonStyle,
  memberSelectorChangeButtonStyle,
  memberSelectorDrawerStyle,
  memberSelectorGridStyle,
  memberSelectorPopoverStyle,
  memberSelectorRadioButtonStyle,
  memberSelectorWebGridStyle,
  memberSelectorWebIconStyle,
} from './MemberSelection.styled';
import { MemberSelectionConfirmationModal } from './MemberSelectionConfirmationModal';

type Props = {
  menuItems: {};
  name: any;
  setActiveMemberIndex: (a: number) => void;
  form: any;
};

export const MemberSelection = ({
  form,
  menuItems,
  name,
  setActiveMemberIndex,
}: Props) => {
  const { ACTIVE_MEMBER_INDEX } = Constants.STORAGE_KEYS.SESSION;
  const currentIndex = storage.session.get(ACTIVE_MEMBER_INDEX);
  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;
  const mobileScreen = useMediaQuery(phoneOnly);
  const memberSelectionLocation: string = 'member selection';
  const adobeLocation: string = 'member change notification';
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState();
  const featureFlags = useLagoon('feature-flags')();
  const enableMemberSelectionNotification = getFeatureFlag(
    featureFlags,
    'MEMBER_SELECTION_NOTIFICATION'
  );
  const [, setCorrelationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );
  const [educationalContent, getEducationalContentStatus] =
    useEducationalContentStatus({
      onCompleted: (data) => {
        data?.headers[OPTUM_CORRELATION_ID_HEADER] &&
          setCorrelationIDSessionStorage(
            data?.headers[OPTUM_CORRELATION_ID_HEADER]
          );
      },
    });

  const enablePcpPopupForSbd = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PCP_POPUP_FOR_SBD
  );

  let memberSelectionStatus =
    educationalContent?.data?.educationalContentStatus
      ?.hasViewedMemberSelectorAlert;

  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();

  if (isEligibleForStandaloneDental && !enablePcpPopupForSbd) {
    memberSelectionStatus = true;
  }

  const { setCoverageType, setChipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      setChipValue: state.setChipValue,
      setCoverageType: state.setCoverageType,
    }))
  );
  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );
  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );
  const setMemberSelectionPopupFlag = useStore(
    StoreKeys.SET_MEMBER_SELECTION_POPUP_FLAG
  );
  const hasMemberSelectionPopupShown = useStore(
    StoreKeys.MEMBER_SELECTION_POPUP_FLAG
  );
  const hsidUuid = useHsidUuid();

  const [, setEducationalContentStatus] = useSaveEducationalContentStatus({
    onCompleted: () => {
      setMemberSelectionPopupFlag(true);
    },
  });

  const [
    isMemberSelectionConfirmationModalOpen,
    setIsMemberSelectionConfirmationModalOpen,
  ] = useState(false);
  const arrowDownDataTestId: string = mobileScreen
    ? 'arrowDownMobileDataTestId'
    : 'one-abyss-popover-trigger';

  const { navigate } = useRouter();
  const { clearSearchFilters, defaultSearchFilters, searchFilters } =
    useContext(SearchFilterContext);

  useEffect(() => {
    if (isOpen) {
      const linkName: string = 'active member selection';
      const linkLocation: string = 'body:search home message';
      const modalName: string = 'member selection';
      handleLinkAndModalTrack(linkName, linkLocation, modalName);
    }
  }, [isOpen]);

  useEffect(() => {
    getEducationalContentStatus({
      variables: { isSuperUser, ...(hsidUuid && { userId: hsidUuid }) },
    });
  }, []);

  const handleMemberSelectionChange = (formValue) => {
    if (isMemberSelectionConfirmationModalOpen) {
      adobeLinkTrackEvent({
        name: 'continue',
        location: `modal:${adobeLocation}`,
        type: 'internal',
        destinationUrl: '',
      });
      setIsMemberSelectionConfirmationModalOpen(false);
      setEducationalContentStore({
        ...educationalContentStore,
        hasViewedContent: educationalContent?.data?.hasViewedContent || true,
      });
      setEducationalContentStatus({
        variables: {
          hasViewedMemberSelectorAlert: true,
          hasViewedContent: educationalContent?.data?.hasViewedContent || true,
          ...(hsidUuid && { userId: hsidUuid }),
          isSuperUser,
        },
      });
    }

    const previousSelectedIndex = storage.session.get(ACTIVE_MEMBER_INDEX);
    if (
      (formValue || selectedValue) !== previousSelectedIndex &&
      JSON.stringify(searchFilters) !== JSON.stringify(defaultSearchFilters)
    ) {
      clearSearchFilters();
    }
    storage.session.set(ACTIVE_MEMBER_INDEX, formValue || selectedValue);
    setActiveMemberIndex(Number(formValue || selectedValue));
    setIsOpen(false);
    adobeLinkTrackEvent({
      name: 'contine button',
      location: `modal:${memberSelectionLocation}`,
      type: 'internal',
      destinationUrl: '',
    });
    navigate('/');
    setChipValue(CareCategories.ALL);
    setCoverageType('');
  };

  const getRelationshipTypeCode = (value: any) =>
    menuItems[0].radios[Number(value)]?.relationshipTypecode.toLowerCase() ||
    '';

  return (
    <React.Fragment>
      {mobileScreen ? (
        <div>
          <Drawer
            css={memberSelectorDrawerStyle}
            data-auto-testid={arrowDownDataTestId}
            data-testId={arrowDownDataTestId}
            header={t('Find care for')}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            position="right"
          >
            <Grid>
              <Grid.Col css={memberSelectorGridStyle} span={12}>
                <RadioGroup
                  data-auto-testid="responsive-member-selector"
                  data-testid="responsive-member-selector"
                  model="member-select"
                  onChange={(e) => {
                    adobeLinkTrackEvent({
                      name: getRelationshipTypeCode(e.target.value),
                      location: `modal:${memberSelectionLocation}`,
                      type: 'radio',
                      destinationUrl: '',
                    });
                  }}
                >
                  <RadioGroup.Column>
                    {menuItems[0].radios.map((member) => (
                      <RadioGroup.Radio
                        css={memberSelectorRadioButtonStyle}
                        data-auto-testid="member-label-value"
                        data-testid="member-label-value"
                        key={member.label}
                        label={
                          <Dob
                            data-auto-testid="member-dob"
                            data-testid="member-dob"
                          >
                            {member.label}
                            <div>{member.dob}</div>
                          </Dob>
                        }
                        value={member.value}
                      />
                    ))}
                  </RadioGroup.Column>
                </RadioGroup>
                <Layout.Group alignLayout="center" width="100%">
                  <Button
                    css={memberSelectorChangeButtonStyle}
                    data-auto-testid="member-selection-change-button"
                    data-testid="member-selection-change-button"
                    onClick={() => {
                      const formValue = form?.getValues('member-select');
                      const shouldHandleMemberSelectionChange =
                        !enableMemberSelectionNotification ||
                        memberSelectionStatus ||
                        hasMemberSelectionPopupShown ||
                        formValue === currentIndex;

                      if (shouldHandleMemberSelectionChange) {
                        handleMemberSelectionChange(formValue);
                      } else if (formValue !== currentIndex) {
                        const linkName: string = 'continue button';
                        const linkLocation: string = `modal:${adobeLocation}`;
                        const modalName: string = adobeLocation;
                        handleLinkAndModalTrack(
                          linkName,
                          linkLocation,
                          modalName
                        );
                        setSelectedValue(formValue);
                        setIsMemberSelectionConfirmationModalOpen(true);
                      }
                    }}
                  >
                    {t('Change')}
                  </Button>
                </Layout.Group>
              </Grid.Col>
            </Grid>
          </Drawer>
          <Button
            aria-haspopup="dialog"
            css={memberSelectorButtonStyle}
            data-auto-testid="closBTM"
            data-testid="closBTM"
            onClick={() => setIsOpen(true)}
            variant="text"
          >
            <WelcomeMessageScreen>
              {name}
              <IconSymbol
                color="$core.color.brand.100"
                icon="keyboard_arrow_down"
              />
            </WelcomeMessageScreen>
          </Button>
        </div>
      ) : (
        <Popover
          aria-describedby="memberSelectionButton"
          content={
            <Grid>
              <Grid.Col css={memberSelectorWebGridStyle} span={12}>
                <RadioGroup
                  data-auto-testid="member-selector"
                  data-testid="member-selector"
                  model="member-select"
                  onChange={(e) => {
                    adobeLinkTrackEvent({
                      name: getRelationshipTypeCode(e.target.value),
                      location: `modal:${memberSelectionLocation}`,
                      type: 'radio',
                      destinationUrl: '',
                    });
                  }}
                >
                  <RadioGroup.Column>
                    {menuItems[0].radios.map((member) => (
                      <RadioGroup.Radio
                        css={memberSelectorRadioButtonStyle}
                        data-auto-testid="member-label-value"
                        data-testid="member-label-value"
                        key={member.label}
                        label={
                          <Dob
                            data-auto-testid="member-dob"
                            data-testid="member-dob"
                          >
                            {member.label}
                            <div>{member.dob}</div>
                          </Dob>
                        }
                        value={member.value}
                      />
                    ))}
                  </RadioGroup.Column>
                </RadioGroup>
                <Layout.Group alignLayout="center" width="100%">
                  <Button
                    css={{
                      margin: '12px 0 15px 10px',
                    }}
                    data-auto-testid="member-selection-change-button"
                    data-testid="member-selection-change-button"
                    onClick={() => {
                      const formValue = form?.getValues('member-select');
                      const shouldHandleMemberSelectionChange =
                        !enableMemberSelectionNotification ||
                        memberSelectionStatus ||
                        hasMemberSelectionPopupShown ||
                        formValue === currentIndex;

                      if (shouldHandleMemberSelectionChange) {
                        handleMemberSelectionChange(formValue);
                      } else if (formValue !== currentIndex) {
                        const linkName: string = 'continue button';
                        const linkLocation: string = `modal:${adobeLocation}`;
                        const modalName: string = adobeLocation;
                        handleLinkAndModalTrack(
                          linkName,
                          linkLocation,
                          modalName
                        );
                        setSelectedValue(formValue);
                        setIsMemberSelectionConfirmationModalOpen(true);
                      }
                    }}
                  >
                    {t('Change')}
                  </Button>
                </Layout.Group>
              </Grid.Col>
            </Grid>
          }
          css={memberSelectorPopoverStyle}
          data-auto-testid="one-abyss-popover-trigger"
          data-testid="one-abyss-popover-trigger"
          onOpenChange={() => {
            form?.setValue('member-select', currentIndex);
            setIsOpen(!isOpen);
          }}
          open={isOpen}
          position="right"
          title={t('Find care for')}
          width={290}
        >
          {name}
          <IconSymbol
            color="$core.color.brand.100"
            css={memberSelectorWebIconStyle}
            data-auto-testid="arrow-down"
            data-testid="arrow-down"
            icon="keyboard_arrow_down"
          />
        </Popover>
      )}
      <MemberSelectionConfirmationModal
        adobeLocation={adobeLocation}
        handleConfirmMemberSelectionChange={handleMemberSelectionChange}
        isModalOpen={isMemberSelectionConfirmationModalOpen}
        setIsModalOpen={setIsMemberSelectionConfirmationModalOpen}
      />
    </React.Fragment>
  );
};
